import { RelayPageDataTable } from "@constellation-academy/epic-ui";
import { usePaginationFragment } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { discountActionsTable_DiscountActionFragment$key } from "@relay/discountActionsTable_DiscountActionFragment.graphql";
import { discountActionsTable_QueryFragment$key } from "@relay/discountActionsTable_QueryFragment.graphql";
import type { DiscountActionsTable_QueryFragmentRefetch } from "@relay/DiscountActionsTable_QueryFragmentRefetch.graphql";
import { DISCOUNT_ACTION_FRAGMENT, QUERY_FRAGMENT } from "./discount-actions-table.graphql";
import { actionButtonWrapperClass } from "./discount-actions-table.styles";
import type { DiscountActionsTableProps } from "./discount-actions-table.types";
import { IdDisplayField } from "../../../../components/id-display-field";
import { useDebounceFilters } from "../../../../hooks/useDebounceFilters";
import { useDiscountActionSearchContext } from "../../discount-actions.context";
import type { DiscountActionFilters } from "../../discount-actions.types";
import { DeleteDiscountActionButton } from "../delete-discount-action-button";
import { DiscountActionsTableHeader } from "../discount-actions-table-header";
import { EditDiscountActionButton } from "../edit-discount-actions-button";

export const DiscountActionsTable = ({
	discountActionsQueryFragment,
	canModifyDiscountActions,
}: DiscountActionsTableProps) => {
	const { t } = useTranslation(["common"]);

	const { filters, isArchivePage } = useDiscountActionSearchContext();

	const { data, refetch } = usePaginationFragment<
		DiscountActionsTable_QueryFragmentRefetch,
		discountActionsTable_QueryFragment$key
	>(QUERY_FRAGMENT, discountActionsQueryFragment);

	useDebounceFilters<DiscountActionFilters>(filters, refetch);

	const connectionId = data.Admin.Billing.DiscountActions.__id;

	return (
		<>
			<RelayPageDataTable<
				DiscountActionsTable_QueryFragmentRefetch,
				discountActionsTable_QueryFragment$key,
				discountActionsTable_DiscountActionFragment$key
			>
				queryFragment={QUERY_FRAGMENT}
				parentFragmentRef={discountActionsQueryFragment}
				nodeInlineFragment={DISCOUNT_ACTION_FRAGMENT}
				extractConnection={(data) => data.Admin.Billing.DiscountActions}
				rowsCount={20}
				emptyMessage={t("discounts.noActions")}
				filters={filters}
				header={<DiscountActionsTableHeader connectionId={connectionId} />}
			>
				{(Column) => (
					<>
						<Column
							header={t("table.id")}
							body={(item) => <IdDisplayField id={item.id} />}
							style={{
								width: "5%",
							}}
						/>
						<Column header={t("table.name")} body={(item) => item.title} />
						<Column
							header={t("discounts.value")}
							body={(item) => `${item.percent}%`}
							style={{
								width: "10%",
							}}
						/>
						<Column
							header={t("discounts.isReductible")}
							body={(item) => item.isReductible}
							style={{
								width: "10%",
							}}
						/>
						<Column
							header={t("discounts.discountType")}
							body={(item) => item.discountType?.kind}
							style={{
								width: "10%",
							}}
						/>
						{canModifyDiscountActions && (
							<Column
								header={t("table.actions")}
								body={(item) => (
									<div className={actionButtonWrapperClass}>
										<EditDiscountActionButton
											id={item.id}
											isArchivePage={isArchivePage}
										/>
										<DeleteDiscountActionButton id={item.id} />
									</div>
								)}
								style={{
									width: "10%",
								}}
							/>
						)}
					</>
				)}
			</RelayPageDataTable>
		</>
	);
};
